import type { ImportMetaLocation } from "@-/dev.improve.haniverse.lib.logger";
import memoize, { memoizeClear } from "@-/com.npmjs.memoize";
import { contentRelations } from "@-/dev.improve.haniverse.database.schemas.content.schema";
import {
  createWorkerQueryExecutors,
  workerQueryCodecs,
} from "@-/dev.improve.haniverse.configs.worker/worker-query-executor";
import {
  createBatchedWorkerQuerySend,
  createSingleWorkerQuerySend,
} from "@-/dev.improve.haniverse.configs.worker/worker-query-batcher";
import { drizzle, type SqliteRemoteDatabase } from "@-/com.npmjs.drizzle-orm/sqlite-proxy";

type ContentRelations = typeof contentRelations;

/**
 * On web the `content.db` database is NOT shipped to the browser. Instead of
 * importing the encrypted/plain content shard into OPFS, the web build queries
 * it remotely from the `haniverse-worker` Cloudflare Worker, which runs SQL against
 * a D1 copy of the same schema via the `?db=content` selector (see
 * apps/haniverse-worker). Mirrors the `static-database.web.ts` approach.
 *
 * The "connection" here is just the worker base URL — the actual transport is
 * `fetch` inside `getContentDatabase`.
 */
export interface ContentDatabaseConnection {
  workerUrl: string;
}

/** Resolved drizzle (remote sqlite-proxy) handle on the content connection. */
// rc.5: `SqliteRemoteDatabase<TRunResult, TRelations>` — the run result comes first.
export type ContentDatabaseHandle = SqliteRemoteDatabase<unknown, ContentRelations>;

/** Getter form of {@link ContentDatabaseHandle}; takes `import.meta.location` (or `null` to opt out of logging). */
export type ContentDatabase = (location: ImportMetaLocation) => ContentDatabaseHandle;

/** Mirrors the native variant so call sites stay uniform. On web `absolutePath` is the worker URL. */
export type ContentDatabaseShard = {
  absolutePath: string;
};

/**
 * `shard.absolutePath` is the worker base URL on web — the shard importer
 * (`ensure-content-database-shards-from-assets.web.ts`) resolves
 * `EXPO_PUBLIC_HANIVERSE_WORKER_URL` and threads it through. Keeping the value
 * here lets `useGetContentDatabase`'s single-flight cache key on it.
 */
export const createContentDatabaseConnection = memoize(
  async (shard: ContentDatabaseShard): Promise<ContentDatabaseConnection> => ({
    workerUrl: shard.absolutePath,
  }),
  { cacheKey: ([shard]) => shard.absolutePath },
);

export function clearContentDatabaseConnectionMemo(): void {
  memoizeClear(createContentDatabaseConnection);
}

/** Web content connections are URL value objects and have nothing to close. */
export function closeContentDatabaseConnection(
  _connection: ContentDatabaseConnection,
): Promise<void> {
  return Promise.resolve();
}

const TRAILING_SLASHES_RE = /\/+$/;

export function getContentDatabase(connection: ContentDatabaseConnection): ContentDatabaseHandle {
  const baseUrl = connection.workerUrl.replace(TRAILING_SLASHES_RE, "");
  // Built ONCE per handle, not per query: the batcher's coalescing queue lives in this closure, so
  // a per-query instance would have nothing to batch with and silently degrade to one request each.
  const send = createBatchedWorkerQuerySend({
    batchUrl: `${baseUrl}/query/batch?db=content`,
    label: "content-db",
    sendSingle: createSingleWorkerQuerySend({
      label: "content-db",
      queryUrl: `${baseUrl}/query?db=content`,
    }),
  });
  // `createWorkerQueryExecutors` routes every statement through `executeWorkerQuery`, so a
  // `where col in (...)` over more than D1's 100-bound-parameter cap is split into several
  // under-cap statements and the row sets concatenated. See static-database.web.ts for the same
  // wiring. The `send` above then coalesces those chunks — and every other read issued in the
  // same tick — into one POST.
  return drizzle<ContentRelations>(createWorkerQueryExecutors(send), {
    codecs: workerQueryCodecs,
    relations: contentRelations,
  });
}

/** Web content is always remote; provided for parity with the native source-selection path. */
export function getOnlineContentDatabase(workerUrl: string): ContentDatabaseHandle {
  return getContentDatabase({ workerUrl });
}
